import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SearchSelect, { SearchSelectItem } from '../atoms/SearchSelect'
import { formatPrice } from 'src/helpers/formatPrice'
import {
  FETCH_MOTORBIKES,
  MarketPlaceMotorbike,
  SET_CURRENT_PAGE,
  SET_SEARCH_TERM,
  SET_STOCK_TAB
} from 'src/redux/motorbikesReducer'
import { RootReducer } from 'src/redux/rootReducer'

interface VehicleSearchSelectProps {
  selectedId: string
  selectedLabel: string
  placeholder: string
  error?: boolean
  onSelect: (motorbike: MarketPlaceMotorbike) => void
  onClear: () => void
}

const yearFromRegistration = (firstRegistration: string | null): string => {
  const year = firstRegistration?.slice(0, 4) ?? ''
  if (/^\d{4}$/.test(year)) {
    return year
  }
  return ''
}

const motorbikeTitle = (motorbike: MarketPlaceMotorbike): string => `${motorbike.brand_name} ${motorbike.model}`.trim()

const motorbikeSubtitle = (motorbike: MarketPlaceMotorbike, kmLabel: string): string => {
  const parts: string[] = []
  const year = yearFromRegistration(motorbike.first_registration)
  if (year.length > 0) {
    parts.push(year)
  }
  if (typeof motorbike.total_mileage === 'number') {
    parts.push(`${formatPrice(motorbike.total_mileage)} ${kmLabel}`)
  }
  const vin = motorbike.vin ?? ''
  if (vin.length > 0) {
    parts.push(vin)
  }
  return parts.join(' · ')
}

const VehicleSearchSelect = ({
  selectedId,
  selectedLabel,
  placeholder,
  error = false,
  onSelect,
  onClear
}: VehicleSearchSelectProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const motorbikes = useSelector((state: RootReducer) => state.motorbikesReducer.motorbikes)
  const loading = useSelector((state: RootReducer) => state.motorbikesReducer.loading)
  const kmLabel = t('my_vehicles.km')
  const items: SearchSelectItem[] = motorbikes.map((motorbike) => ({
    id: motorbike.id,
    title: motorbikeTitle(motorbike),
    subtitle: motorbikeSubtitle(motorbike, kmLabel)
  }))

  const handleOpen = useCallback(() => {
    dispatch(SET_STOCK_TAB('in_stock'))
    dispatch(SET_CURRENT_PAGE(1))
    dispatch(SET_SEARCH_TERM(''))
    dispatch(FETCH_MOTORBIKES())
  }, [dispatch])

  const handleClose = useCallback(() => {
    dispatch(SET_SEARCH_TERM(''))
  }, [dispatch])

  const handleSearchChange = useCallback(
    (value: string) => {
      dispatch(SET_SEARCH_TERM(value))
      dispatch(SET_CURRENT_PAGE(1))
      dispatch(FETCH_MOTORBIKES())
    },
    [dispatch]
  )

  const handleSelect = (item: SearchSelectItem): void => {
    const motorbike = motorbikes.find((entry) => entry.id === item.id) ?? null
    if (motorbike !== null) {
      onSelect(motorbike)
    }
  }

  return (
    <SearchSelect
      selectedId={selectedId}
      selectedLabel={selectedLabel}
      placeholder={placeholder}
      searchPlaceholder={t('my_vehicles.search_purchased')}
      loadingLabel={t('my_vehicles.loading_offers')}
      emptyLabel={t('my_vehicles.no_purchased')}
      error={error}
      items={items}
      loading={loading}
      onOpen={handleOpen}
      onClose={handleClose}
      onSearchChange={handleSearchChange}
      onSelect={handleSelect}
      onClear={onClear}
    />
  )
}

export default VehicleSearchSelect
